import { DEFAULT_PROJECT_NAME } from 'constants/projects';
import { createActionCreator, createReducer } from 'deox';
import { AppState } from 'types/AppState';
import { ProjectData, ProjectFileBase, StoredProjectFiles } from 'types/ProjectFiles';

const prefix = 'PROJECT_FILES';
const defaultState: StoredProjectFiles = {
  list: null,
  current: '',
  status: 'saved'
};

// SagaActions:

export const createProjectFile = createActionCreator(`${prefix}/CREATE`);

export const createNewProjectFile = createActionCreator(`${prefix}/CREATE_NEW`);

export const replaceProjectFile = createActionCreator(`${prefix}/REPLACE`);

export const saveProjectFile = createActionCreator(`${prefix}/SAVE`);

export const selectProjectFile = createActionCreator(`${prefix}/SELECT`, resolve => (id: string) =>
  resolve(id)
);

export const getProjectFilesList = createActionCreator(
  `${prefix}/GET_LIST`,
  resolve => (userId: number) => resolve(userId)
);

export const deleteProjectFile = createActionCreator(`${prefix}/DELETE`, resolve => (id: string) =>
  resolve(id)
);

// ReducerActions:

export const setProjectFileList = createActionCreator(
  `${prefix}/SET_LIST`,
  resolve => (list: ProjectFileBase[]) => resolve(list)
);

export const addProjectFile = createActionCreator(
  `${prefix}/ADD`,
  resolve => (pF: ProjectFileBase) => resolve(pF)
);

export const updateStoredProjectFile = createActionCreator(
  `${prefix}/UPDATE_STORE`,
  resolve => (id: string, data: Partial<ProjectFileBase>) => resolve({ id, data })
);

export const setCurrentProjectFile = createActionCreator(
  `${prefix}/SET_CURRENT`,
  resolve => (id: string) => resolve(id)
);

export const setStatus = createActionCreator(
  `${prefix}/SET_STATUS`,
  resolve => (status: StoredProjectFiles['status']) => resolve(status)
);

export const deleteStoredProjectFile = createActionCreator(
  `${prefix}/DELETE_STORED`,
  resolve => (id: string) => resolve(id)
);

export const changeProjectFileName = createActionCreator(
  `${prefix}/CHANGE_NAME`,
  resolve => (id: string, name: string) => resolve({ id, name })
);

export const applyProject = createActionCreator(
  `${prefix}/APPLY_PROJECT`,
  resolve => (template: ProjectData) => resolve(template)
);

export const projectFiles = createReducer(defaultState, handle => [
  handle(addProjectFile, (state, { payload }) => ({ ...state, list: [...state.list, payload] })),
  handle(setProjectFileList, (state, { payload }) => ({ ...state, list: payload })),
  handle(updateStoredProjectFile, (state, { payload: { id, data } }) => ({
    ...state,
    list: state.list.map(item => (item.id === id ? { ...item, ...data } : item))
  })),
  handle(setStatus, (state, { payload }) => ({ ...state, status: payload })),
  handle(setCurrentProjectFile, (state, { payload }) => ({ ...state, current: payload })),
  handle(deleteStoredProjectFile, (state, { payload }) => ({
    ...state,
    list: state.list.filter(pf => pf.id !== payload)
  })),
  handle(changeProjectFileName, (state, { payload: { id, name } }) => ({
    ...state,
    list: state.list.map(item => (item.id === id ? { ...item, name } : item))
  }))
]);

export const getStoreProjectFilesList = (state: AppState): ProjectFileBase[] =>
  state.projectFiles.list;
export const getCurrentProjectId = (state: AppState): StoredProjectFiles['current'] =>
  state.projectFiles.current;
export const getStatus = (state: AppState): StoredProjectFiles['status'] =>
  state.projectFiles.status;
export const getProjectById = (id: string) => (state: AppState): ProjectFileBase =>
  state.projectFiles.list.find((item: ProjectFileBase) => item.id === id);

export const getCurrentProjectName = (state: AppState) => {
  const id = getCurrentProjectId(state);
  if (!id) return DEFAULT_PROJECT_NAME;
  return getProjectById(id)(state)?.name;
};
